/**
 * <BottomNav />
 *
 * App-style fixed bottom navigation for mobile. Visible only below the `md`
 * breakpoint. Four tabs: Home (Marketplace), Forums, Sell (primary CTA),
 * Profile. The "Sell" tab is visually emphasized in brand red so the primary
 * revenue-generating action is always one tap away.
 *
 * Design decisions:
 *   * 56px icon cells -- far above the 48px WCAG minimum.
 *   * Labels always visible -- no "icon-only" surprise for senior users.
 *   * `aria-current="page"` on the active tab for screen readers.
 *   * `safe-area-inset-bottom` respected via Tailwind's `pb-[env(...)]`.
 */
import * as React from "react";
import { NavLink } from "react-router-dom";
import { Home, MessagesSquare, Plus, UserRound } from "lucide-react";
import { cn } from "../../lib/cn";

type Item = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  emphasize?: boolean;
};

const items: Item[] = [
  { to: "/", label: "Home", icon: Home },
  { to: "/forums", label: "Forums", icon: MessagesSquare },
  { to: "/sell", label: "Sell", icon: Plus, emphasize: true },
  { to: "/profile", label: "Profile", icon: UserRound },
];

export function BottomNav() {
  return (
    <nav
      aria-label="Primary mobile navigation"
      data-testid="bottom-nav"
      className={cn(
        "md:hidden fixed bottom-0 inset-x-0 z-40",
        "bg-white/95 backdrop-blur-lg border-t border-surface-border",
        "pb-[env(safe-area-inset-bottom)]",
      )}
    >
      <ul className="flex items-stretch justify-around">
        {items.map(({ to, label, icon: Icon, emphasize }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={to === "/"}
              data-testid={`bottom-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center justify-center gap-1 py-2.5 btn-target",
                  isActive ? "text-brand" : "text-ink-muted hover:text-ink",
                )
              }
              aria-current={undefined}
            >
              {({ isActive }) => (
                <>
                  <span
                    aria-hidden="true"
                    className={cn(
                      "flex items-center justify-center rounded-full transition-colors",
                      emphasize
                        ? "h-12 w-12 bg-brand text-white shadow-card"
                        : "h-10 w-10",
                      emphasize && isActive && "bg-brand-hover",
                    )}
                  >
                    <Icon className={cn(emphasize ? "h-6 w-6" : "h-6 w-6")} />
                  </span>
                  <span className="text-[11px] font-semibold tracking-wide uppercase">
                    {label}
                  </span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
